import type { UploadJob } from '@/stores/uploadJobStore';

/**
 * Which upload job the recording page should treat as its own.
 * Pure so it can be unit-tested without the store or a router.
 */
export interface PageJobContext {
  storageId?: string | null;
  isEncounterMode: boolean;
  encounterId?: string | null;
  appointmentId?: string | null;
  // Keys of jobs the page observed in a non-terminal phase during the current mount. A
  // completed job is only adopted if it is here, so the page never auto-opens (or paints
  // "Redirecting") for a job it did not witness finish.
  seenActiveKeys?: ReadonlySet<string>;
}

export function selectPageJob(
  jobs: Record<string, UploadJob>,
  ctx: PageJobContext
): UploadJob | null {
  const all = Object.values(jobs);

  // Fast path for the live recorder: the encrypted bytes are still page-local, so match by
  // their storage id. This keeps the record flow byte-for-byte unchanged.
  if (ctx.storageId) {
    const byStorage = all.find((j) => j.storageId === ctx.storageId);
    if (byStorage) return byStorage;
  }

  // No page-local bytes (re-entry, or an uploaded file that never touched the recorder): match
  // this page's own job by server identity. A completed job is only adopted if the page saw it
  // active this mount, so a job that finished while the page was elsewhere is left to the sidebar
  // rather than auto-opened or shown mid-"Redirecting". Newest wins so a stale run is not used.
  const seenActiveKeys = ctx.seenActiveKeys;
  const matches = all.filter((j) => {
    if (j.phase === 'cancelled') return false;
    if (j.phase === 'complete' && !seenActiveKeys?.has(j.key)) return false;
    return ctx.isEncounterMode
      ? !!ctx.encounterId && j.encounterId === ctx.encounterId
      : !!ctx.appointmentId && j.appointmentId === ctx.appointmentId;
  });

  return matches.reduce<UploadJob | null>(
    (newest, j) => (newest === null || j.startedAt > newest.startedAt ? j : newest),
    null
  );
}
